<script setup>
import { storeToRefs } from 'pinia';
import { useMembers } from '~/common/composables/members.js';
import { useCommonStore } from '~/common/stores/common.store';
import { useProjectManagementStore } from '~/project-management/store/pm.store.js';

const emit = defineEmits(['add-tracking']);

const common_store = useCommonStore();
const project_management_store = useProjectManagementStore();
const { getUserDetails } = useMembers();
const { is_type_user } = common_store;
const { active_schedule, active_task } = storeToRefs(project_management_store);

const $t = inject('$t');
const $date = inject('$date');

const state = reactive({
  is_section_open: true,
});

const activity_trackings = computed(() => {
  return active_schedule.value?.trackings?.filter?.(activity_tracking => activity_tracking.activity === active_task.value.uid)?.sort?.((a, b) => new Date(b.start) - new Date(a.start));
});
</script>

<template>
  <div v-if="activity_trackings.length" class="py-4">
    <div class="flex items-center gap-2">
      <div class="cursor-pointer flex items-center" @click="state.is_section_open = !state.is_section_open">
        <IconHawkChevronDown v-if="state.is_section_open" class="inline" />
        <IconHawkChevronRight v-else class="inline" />
        <span class="text-sm font-semibold text-gray-900 ml-1">
          {{ $t('Activity tracking') }}
        </span>
      </div>
      <div
        v-if="active_task.actions.track"
        class="w-5 h-5 bg-primary-50 hover:bg-primary-100 flex items-center justify-center rounded-full cursor-pointer"
        @click="emit('add-tracking')"
      >
        <IconHawkPlus class="w-3 h-3 text-primary-700" />
      </div>
    </div>
    <div v-if="state.is_section_open" class="mt-4 ml-4">
      <div class="grid grid-cols-8 pl-2 mb-1 text-sm font-medium text-gray-900">
        <div class="col-span-3">
          {{ $t('Resource') }}
        </div>
        <div class="col-span-2">
          {{ $t('Date') }}
        </div>
        <div class="col-span-1">
          {{ $t('Work') }}
        </div>
        <div class="col-span-1">
          {{ $t('Units') }}
        </div>
        <div class="col-span-1">
          {{ $t('Cost') }}
        </div>
      </div>
      <div v-for="tracking in activity_trackings" :key="tracking.uid">
        <div class="grid grid-cols-8 pl-2 hover:bg-gray-50 rounded-lg text-xs font-normal text-gray-600 items-center min-h-9 group">
          <div class="col-span-3">
            <template v-if="tracking.resource">
              <template v-if="active_schedule.resources.find(resource => resource.uid === tracking.resource)">
                <template v-if="active_schedule.resources.find(resource => resource.uid === tracking.resource).type === 'custom'">
                  <HawkBadge size="md" custom_classes="!p-[3px] !h-full bg-gray-100 text-gray-100">
                    <div class="flex items-center text-xs font-medium text-gray-700">
                      <div class="w-4 h-4 flex items-center justify-center bg-gray-100 text-xs font-medium text-gray-600 rounded-full border border-gray-600 ">
                        {{ active_schedule.resources.find(resource => resource.uid === tracking.resource).name.charAt(0) }}
                      </div>
                      <div class="mx-1.5">
                        {{ active_schedule.resources.find(resource => resource.uid === tracking.resource).name }}
                      </div>
                    </div>
                  </HawkBadge>
                </template>
                <template v-else-if="active_schedule.resources.find(resource => resource.uid === tracking.resource).type === 'member'">
                  <template v-if="is_type_user(active_schedule.resources.find(resource => resource.uid === tracking.resource).external_id)">
                    <HawkMembers
                      :members="active_schedule.resources.find(resource => resource.uid === tracking.resource).external_id"
                      :has_avatar="true"
                      type="badge"
                      size="xs"
                    />
                  </template>
                  <template v-else>
                    <HawkMembers
                      :members="active_schedule.resources.find(resource => resource.uid === tracking.resource).external_id"
                      :has_avatar="true"
                      type="badge"
                      size="xs"
                    />
                  </template>
                </template>
              </template>
              <div v-else class="italic text-gray-400 text-xs">
                {{ $t('Deleted') }}
              </div>
            </template>
            <template v-else>
              <div class="italic text-gray-400 text-xs">
                &mdash;
              </div>
            </template>
          </div>
          <div class="col-span-2">
            {{ $date(tracking.start.slice(0, 10), 'DD MMMM YYYY') }}
          </div>
          <div class="col-span-1">
            <template v-if="tracking.work">
              {{ tracking.work }} {{ $t('units') }}
            </template>
            <template v-else>
              &mdash;
            </template>
          </div>
          <div class="col-span-1">
            <template v-if="tracking.units">
              {{ tracking.units }} {{ active_schedule.resources.find(resource => resource.uid === tracking.resource)?.cost_type === 'per_hour' ? $t('hrs') : $t('pcs') }}
            </template>
            <template v-else>
              &mdash;
            </template>
          </div>
          <div class="col-span-1">
            <div class="flex justify-between mr-3">
              <div>
                <template v-if="tracking.cost">
                  {{ active_schedule.currency.symbol }}{{ new Intl.NumberFormat('en-US').format(tracking.cost) }}
                </template>
                <template v-else>
                  &mdash;
                </template>
              </div>
              <IconHawkInfoCircle
                v-tippy="`${$t('Created by')} ${getUserDetails(tracking.created_by?.uid).members_details[0].name} ${$t('on')} ${$date(tracking.created_at, 'DD MMMM YYYY')}`"
                class="size-4 invisible group-hover:visible"
              />
            </div>
          </div>
        </div>
        <hr class="border-gray-200 pl-2 mr-2 ml-2">
      </div>
    </div>
  </div>
</template>
